import { plainToClass } from "class-transformer";
import { LMSTrainingOverviewDto, LMSSiteStudyUserTrainingDto, LMSUserTrainingDetailDto, LMSTrainingStatus, LMSUserTrainingAssignmentStateDto, LMSRoleDto, LMSStudyDto, siteIdParam, studyIdParam } from "./trainings.objects";
import { MultiFileUploadManager } from "./multi-file-upload-manager";
import { InitializeFilterDropdown, GetSelectedDropdownValues } from "../../../../shared/scripts/filtering/filtering.utilities";
import { IFilter } from "../../../../shared/scripts/filtering/filtering.entities";

export class LMSSiteStudyUserDetailsPage implements IFilter {
    userTrainingsTable: any;
    addRemoveTrainingsTable: any;
    materialsUploadManager: MultiFileUploadManager;
    allUserTrainings: LMSSiteStudyUserTrainingDto[] = [];
    userTrainings: LMSSiteStudyUserTrainingDto[] = [];
    allTrainings: LMSTrainingOverviewDto[];
    trainings: LMSTrainingOverviewDto[];
    private editingTrainingId: number | null = null;
    private editingTraining: LMSUserTrainingDetailDto | null = null;
    private statusFilter: LMSTrainingStatus | null = null;
    private assignmentStates: Map<number, LMSUserTrainingAssignmentStateDto>;
    private initialAssignments: Map<number, boolean>;
    private pendingAssignments: Map<number, boolean>;

    private static readonly statusConfig: { status: LMSTrainingStatus; label: string }[] = [
        { status: "pending", label: "Pending" },
        { status: "overdue", label: "Overdue" },
        { status: "complete", label: "Complete" },
        { status: "expired", label: "Expired" }
    ];

    constructor(
        private siteId: number | null,
        private studyId: number | null,
        private userId: number,
        allTrainingsJson: any,
        userTrainingAssignmentStatesJson: any
    ) {
        this.allTrainings = plainToClass(LMSTrainingOverviewDto, <LMSTrainingOverviewDto[]>allTrainingsJson).filter(t => !t.isArchived);
        this.trainings = this.allTrainings;

        var assignmentStates = plainToClass(LMSUserTrainingAssignmentStateDto, <LMSUserTrainingAssignmentStateDto[]>userTrainingAssignmentStatesJson ?? []);
        this.assignmentStates = new Map(assignmentStates.map(a => [a.trainingId, a]));

        this.loadData();
        this.initializeTable();
        this.initializeCompletionDialog();
        this.initializeAddRemoveDialog();
        this.initializeDialogFilters();

        $('#training-progress-bar').on('click', '.lms-progress-segment', (e) => {
            const status = $(e.currentTarget).data('status') as LMSTrainingStatus;
            this.toggleStatusFilter(status);
        });

        $('#reset-progress-bar').on('click', () => this.resetStatusFilter());

        $("#addRemoveTrainingsBtn").on('click', () => this.openAddRemoveDialog());
    }

    private loadData(): void {
        $.get(`?handler=UserTrainings${siteIdParam(this.siteId)}${studyIdParam(this.studyId)}&userId=${this.userId}`)
            .done((result: LMSSiteStudyUserTrainingDto[]) => {
                this.allUserTrainings = plainToClass(LMSSiteStudyUserTrainingDto, result ?? []);
                this.renderProgressBar();
                this.applyStatusFilter();
            });
    }

    // ===== User Details Table =====
    private initializeTable(): void {
        this.userTrainingsTable = $('#lmsUserSiteStudyTrainingsTable').DataTable({
            dom: 'rtip',
            autoWidth: false,
            info: false,
            paging: false,
            search: true,
            scrollX: true,
            scrollY: '60vh',
            scrollCollapse: true,
            order: [[1, 'asc']],
            columns: [
                { data: "trainingName", title: "Training Name", className: "text-nowrap" },
                {
                    data: "daysRemaining",
                    title: "Days Remaining",
                    render: (data: number, type: string, row: LMSSiteStudyUserTrainingDto) => {
                        if (type === 'sort' || type === 'type') {
                            return data;
                        }
                        return row.isComplete ? "" : data > -1 ? `${data} days` : `${-data} days overdue`;
                    }
                },
                { data: "expirationDate", title: "Expiration Date" },
                { data: "completedAt", title: "Completed Date" },
                {
                    data: "isComplete", title: "Completed",
                    render: function (data: boolean) {
                        if (data) {
                            return '<i class="fa-solid fa-check text-success text-center"></i>';
                        }
                        return '<i class="fa-solid fa-xmark text-danger text-center"></i>';
                    }
                },
                {
                    data: null,
                    title: "",
                    className: "text-center lms-table-col-actions",
                    orderable: false,
                    render: function () {
                        return `<button class="lms-edit-training"><i class="fa-solid fa-pencil"></i></button>`;
                    }
                }
            ],
            data: []
        });

        $("#lmsUserSiteStudyTrainingsTable tbody").on('click', '.lms-edit-training', (e) => {
            var row = $(e.currentTarget).closest('tr');
            var data = $("#lmsUserSiteStudyTrainingsTable").DataTable().row(row).data();
            if (!data || !data.trainingId) return;

            this.openEditDialog(data.userTrainingId);
        })
    }

    // ===== Edit User Training Dialog =====
    private initializeCompletionDialog(): void {
        this.materialsUploadManager = new MultiFileUploadManager("#files-input", "#files-list");

        document.getElementById("files-add-btn").addEventListener("click", () => {
            document.getElementById("files-input").click();
        });

        $('#userTrainingFormDialog').on('hidden.bs.modal', () => {
            this.materialsUploadManager.reset();
            $("#completeTrainingPassword").val("");
            this.editingTraining = null;
        })

        $("#complete-training-checkbox").on('change', () => {
            this.autoPopulateExpirationDate();
            this.updateCompletePasswordVisibility();
        });

        $("#saveUserTrainingBtn").on('click', () => this.submitUserTrainingForm());

        // Every button here is type="button", but the password box is the only field that blocks
        // implicit submission, so Enter in it natively GET-submits the form and wipes the page's
        // query string. Route any submit into the same AJAX save the Save button uses.
        $('#userTrainingForm').on('submit', (e) => {
            e.preventDefault();
            this.submitUserTrainingForm();
        });
    }

    private openEditDialog(trainingId: number) {
        $("#userTrainingFormDialog .is-invalid").removeClass("is-invalid");
        $(".invalid-feedback").text("").hide();

        this.editingTrainingId = trainingId;
        $.get(`?handler=UserTrainingDetails&id=${trainingId}`)
            .done((training: LMSUserTrainingDetailDto) => {
                this.editingTraining = training;
                $("#userTrainingDialogTitle").text(training.trainingName).show();
                $("#files-container").toggle(training.requiresUpload);
                this.populateForm(training);
                this.updateCompletePasswordVisibility();
                $('#userTrainingFormDialog').modal('show');
            })
        //.fail(() => this.showServerError());
    }

    private populateForm(t: LMSUserTrainingDetailDto) {
        if (t.requiresUpload) {
            this.materialsUploadManager.setExistingMaterials(t.uploads);
        }

        $("#dueDate").val(t.dueDate?.substring(0, 10));
        $("#expirationDate")
            .prop("disabled", !t.canExpire)
            .val(t.canExpire ? t.expirationDate?.substring(0, 10) : "");
        $("#notes").val(t.notes);
        $('#complete-training-checkbox').prop('checked', t.isComplete);
    }

    private autoPopulateExpirationDate(): void {
        const isChecked = $("#complete-training-checkbox").prop("checked");
        const expirationDateInput = $("#expirationDate");

        if (isChecked && this.editingTraining?.canExpire && !expirationDateInput.val()) {
            const expiration = new Date();
            expiration.setDate(expiration.getDate() + this.editingTraining.defaultExpirationTimeInDays);
            expirationDateInput.val(expiration.toISOString().substring(0, 10));
        }
    }

    private updateCompletePasswordVisibility(): void {
        // Password confirmation is only needed when this action is the training isn't it's already complete.
        const isNewlyCompleted = !this.editingTraining?.isComplete && $("#complete-training-checkbox").prop("checked");
        $("#complete-password-container").prop("hidden", !isNewlyCompleted);
        if (!isNewlyCompleted) {
            $("#completeTrainingPassword").val("");
        }
    }

    private submitUserTrainingForm() {
        if (!this.validate()) {
            return;
        }

        $("#saveTrainingSpinner").show();

        const isComplete = $("#complete-training-checkbox").prop("checked");
        const isNewlyCompleted = !this.editingTraining?.isComplete && isComplete;

        if (isNewlyCompleted) {
            this.markTrainingComplete()
                .done(() => this.saveTrainingDates())
                .fail((e) => {
                    $("#saveTrainingSpinner").hide();
                    this.markInvalid(
                        $("#completeTrainingPassword"),
                        e.responseJSON?.message ?? "Unable to verify your password."
                    );
                });
        } else {
            this.saveTrainingDates();
        }
    }

    private markTrainingComplete() {
        return $.ajax({
            url: `?handler=MarkComplete&id=${this.editingTrainingId}`,
            method: 'POST',
            headers: { 'RequestVerificationToken': $('input[name="__RequestVerificationToken"]').val() as string },
            data: {
                password: $("#completeTrainingPassword").val()
            }
        });
    }

    private saveTrainingDates() {
        const payload = this.buildPayload(this.editingTrainingId);

        $.ajax({
            url: `?handler=UpdateUserTraining`,
            method: 'POST',
            contentType: false,
            processData: false,
            headers: { 'RequestVerificationToken': $('input[name="__RequestVerificationToken"]').val() as string },
            data: payload
        }).done(() => {
            $("#saveTrainingSpinner").hide();
            $('#userTrainingFormDialog').modal('hide');
            this.loadData();
        }).fail((e) => {
            $("#saveTrainingSpinner").hide();
            console.log(e);
        });
    }

    private validate(): boolean {
        $("#userTrainingFormDialog .is-invalid").removeClass("is-invalid");
        $(".invalid-feedback").text("").hide();

        let isValid = true;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const expEl = $("#expirationDate");
        const expVal = expEl.val() as string;
        const isComplete = $("#complete-training-checkbox").prop("checked");
        if (isComplete && this.editingTraining?.canExpire) {
            if (!expVal) {
                this.markInvalid(expEl, "Expiration date is required.");
                isValid = false;
            } else if (expVal && new Date(expVal) < today) {
                this.markInvalid(expEl, "Expiration date must be on or after today.");
                isValid = false;
            }
        }

        const dueEl = $("#dueDate");
        const dueVal = dueEl.val() as string;
        if (!dueVal) {
            this.markInvalid(dueEl, "Due date is required.");
            isValid = false;
        }

        const fileEl = $("#files-list");
        if (this.editingTraining?.requiresUpload && isComplete && fileEl.children().length < 1) {
            this.markInvalid(fileEl, "At least one file is required.");
            isValid = false;
        }

        const passEl = $("#completeTrainingPassword");
        const isNewlyCompleted = !this.editingTraining?.isComplete && isComplete;
        if (isNewlyCompleted && !passEl.val()) {
            this.markInvalid(passEl, "Please enter your password to verify your identity.");
            isValid = false;
        }

        return isValid;
    }

    // ===== Add/Remove User Trainings Dialog =====
    private initializeAddRemoveDialog(): void {
        this.addRemoveTrainingsTable = $('#lmsTrainingTable').DataTable({
            dom: 'rtip',
            autoWidth: false,
            info: false,
            paging: false,
            search: true,
            scrollX: true,
            scrollY: '60vh',
            scrollCollapse: true,
            orderCellsTop: true,
            order: [[0, 'asc']], // order alphabetically by title
            columns: [
                { data: "trainingName", title: "Training Name", className: "text-nowrap" },
                {
                    data: "roles",
                    title: "Roles",
                    render: (data: LMSRoleDto[]) => data.map(r => r.name).join(", ")
                },
                {
                    data: "isStudyIndependent",
                    title: "Study Dependent",
                    render: function (data: boolean) {
                        if (data) {
                            return '<i class="fa-solid fa-xmark text-danger text-center"></i>';
                        }

                        return '<i class="fa-solid fa-check text-success text-center"></i>';
                    }
                },
                {
                    data: "studies",
                    title: "Study",
                    render: (data: LMSStudyDto[]) => data.map(s => s.name).join(", ")
                },
                { data: "timeToComplete", title: "Time to Complete" },
                { data: "expirationTime", title: "Expiration Time" },
                {
                    data: null,
                    title: "",
                    className: "text-center lms-table-col-actions",
                    orderable: false,
                    width: "50px",
                    render: function (data, type, row) {
                        return `<input type="checkbox" class="training-assign-check mb-0" data-training-id="${row.trainingId}" ${row.isAssigned ? 'checked' : ''}>`
                    }
                }
            ],
            data:[]
        });

        $('#lmsTrainingTable tbody').on('change', '.training-assign-check', (e) => {
            const trainingId = $(e.currentTarget).data('training-id');
            const checked = $(e.currentTarget).is(':checked');
            this.pendingAssignments.set(trainingId, checked);
        });

        $('#addRemoveTrainingsDialog').on('shown.bs.modal', () => {
            this.addRemoveTrainingsTable.columns.adjust().draw();
        });

        $("#saveChangesBtn").on('click', () => this.saveAddRemoveChanges());

        $('#addRemoveTrainingsForm').on('submit', (e) => {
            e.preventDefault();
            this.saveAddRemoveChanges();
        });
    }

    private initializeDialogFilters() {
        InitializeFilterDropdown("#study-affiliation", [], this);
        InitializeFilterDropdown("#study-dependent", [], this);
        InitializeFilterDropdown('#role', [], this);
    }

    public triggerFilter(): void {
        const selectedStudyIds = GetSelectedDropdownValues('#study-affiliation');
        const selectedDependent = GetSelectedDropdownValues('#study-dependent');
        const selectedRoleIds = GetSelectedDropdownValues('#role');

        this.trainings = this.allTrainings.filter(t =>
            t.matchesFilter(selectedStudyIds, selectedDependent, selectedRoleIds)
        );
        this.updateTable(this.trainings);
    }

    private updateTable(matchingItems: LMSTrainingOverviewDto[]): void {
        this.addRemoveTrainingsTable.clear();
        this.addRemoveTrainingsTable.rows.add(matchingItems).draw();
    }

    private openAddRemoveDialog(): void {
        this.initialAssignments = new Map(
            Array.from(this.assignmentStates.values()).map(a => [a.trainingId, a.isActive])
        );
        this.pendingAssignments = new Map(this.initialAssignments);

        const rows = this.trainings.map(training => ({
            ...training,
            isAssigned: this.initialAssignments.get(training.trainingId) ?? false
        }));

        const trainingsTable = $('#lmsTrainingTable').DataTable();
        trainingsTable.clear();
        trainingsTable.rows.add(rows).draw();
    }

    private saveAddRemoveChanges(): void {
        const addIds: number[] = [];
        const removeIds: number[] = [];

        $("#saveAddRemoveTrainingsSpinner").show();

        this.pendingAssignments.forEach((isAssigned, trainingId) => {
            if (isAssigned === this.initialAssignments.get(trainingId)) return;
            (isAssigned ? addIds : removeIds).push(trainingId);
        });

        if (addIds.length === 0 && removeIds.length === 0) {
            $('#addRemoveTrainingsDialog').modal('hide');
            return;
        }

        $.ajax({
            url: `?handler=UpdateUserTrainingAssignments`,
            method: 'POST',
            traditional: true,
            headers: { 'RequestVerificationToken': $('input[name="__RequestVerificationToken"]').val() as string },
            data: { userId: this.userId, addIds, removeIds }
        }).done(() => {
            addIds.forEach(id => this.applyAssignmentChange(id, true));
            removeIds.forEach(id => this.applyAssignmentChange(id, false));
            $("#saveAddRemoveTrainingsSpinner").hide();
            $('#addRemoveTrainingsDialog').modal('hide');
            this.loadData();
        }).fail((e) => {
            $("#saveAddRemoveTrainingsSpinner").hide();
            console.log(e);
        });
    }

    // Patches the cached assignment state in place so re-opening this dialog (without a page
    // reload) reflects the just-saved changes instead of the stale state from page load.
    private applyAssignmentChange(trainingId: number, isActive: boolean): void {
        const state = this.assignmentStates.get(trainingId);
        if (state) {
            state.isActive = isActive;
            state.hasAdminOverride = true;
        } else {
            const newState = new LMSUserTrainingAssignmentStateDto();
            newState.trainingId = trainingId;
            newState.userTrainingId = null;
            newState.isActive = isActive;
            newState.hasAdminOverride = true;
            this.assignmentStates.set(trainingId, newState);
        }
    }

    private markInvalid(element: JQuery, errorMessage: string) {
        const formGroup = element.closest('.form-group');

        if (element.is(":checkbox")) {
            formGroup.find(".lms-form-check").addClass("is-invalid");
        } else {
            element.addClass("is-invalid");
        }

        formGroup.find(".invalid-feedback").text(errorMessage).show();
    }

    private buildPayload(id: number): FormData {
        const formData = new FormData();

        formData.append("Id", id.toString());
        formData.append("DueDate", $("#dueDate").val() as string);

        const expVal = $("#expirationDate").val() as string;
        if (expVal) {
            formData.append("ExpirationDate", expVal);
        }

        formData.append("IsComplete", $("#complete-training-checkbox").prop("checked"));

        this.materialsUploadManager.getFiles().forEach(file => {
            formData.append("Materials", file, file.name);
        })

        this.materialsUploadManager.getRemovedMaterialIds().forEach(id => {
            formData.append("RemovedMaterialIds", id.toString());
        })

        return formData;
    }

    // ===== Status Filter =====
    private toggleStatusFilter(status: LMSTrainingStatus): void {
        this.statusFilter = this.statusFilter === status ? null : status;
        this.renderProgressBar();
        this.applyStatusFilter();
        this.updateResetButtonState();
    }

    private resetStatusFilter(): void {
        if (!this.statusFilter) {
            return;
        }

        this.statusFilter = null;
        this.renderProgressBar();
        this.applyStatusFilter();
        this.updateResetButtonState();
    }

    private updateResetButtonState(): void {
        $('#reset-progress-bar').prop('disabled', !this.statusFilter);
    }

    private applyStatusFilter(): void {
        this.userTrainings = this.statusFilter
            ? this.allUserTrainings.filter(t => t.getStatus() === this.statusFilter)
            : this.allUserTrainings;

        this.userTrainingsTable.clear();
        this.userTrainingsTable.rows.add(this.userTrainings).draw();
    }

    private renderProgressBar(): void {
        const counts: Record<LMSTrainingStatus, number> = { pending: 0, overdue: 0, complete: 0, expired: 0 };
        this.allUserTrainings.forEach(t => counts[t.getStatus()]++);

        const total = this.allUserTrainings.length;

        const segments = LMSSiteStudyUserDetailsPage.statusConfig.map(({ status, label }) => {
            const count = counts[status];
            const percent = total > 0 ? (count / total) * 100 : 0;
            const classes = [
                'lms-progress-segment',
                `lms-progress-segment-${status}`,
                count > 0 ? 'has-count' : '',
                this.statusFilter === status ? 'active' : ''
            ].filter(c => c).join(' ');

            if (percent > 0) {
                return `<div class="${classes}" style="width:${percent}%" data-status="${status}">${count} ${label}</div>`;
            }
        }).join('');

        $('#training-progress-bar').html(`
            <div class="lms-progress-track">${segments}</div>
        `);
    }
}
